import { useGlobalToasts } from '@talos/kyoko';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPreTradeRoute } from '../../../containers/Routes/routes';
import { useAppStateDispatch, useAppStateSelector, useAppStateStore } from '../../../providers/AppStateProvider';
import type { AppState } from '../../../providers/AppStateProvider/types';
import { orderSliceToOrderRequestForPreTrade } from '../../OMS/NewOrder/orderSliceToOrderRequest';
import { useRequestPreTradeMutation, type PreTradeRequest } from '../redux/preTradeApi';
import { clearUserSelectedReference, setActiveSession } from '../redux/preTradeSlice';
import { PreTradeTabType } from '../types';
import { usePreTradeSessionConfirmation } from './usePreTradeSessionConfirmation';

/**
 * Hook that handles opening the Pre-Trade panel.
 * Uses the same order request builder as actual order submission.
 */
export function useOpenPreTradePanel() {
  const dispatch = useAppStateDispatch();
  const store = useAppStateStore();
  const navigate = useNavigate();
  const [requestPreTrade, { isLoading: isPreTradeLoading }] = useRequestPreTradeMutation();
  const { resolveSessionID: resolveSession, ConfirmationDialog } = usePreTradeSessionConfirmation();
  const { add: addToast } = useGlobalToasts();

  const layoutId = useAppStateSelector((state: AppState) => state.appLayout.currentLayout?.id);

  const openPreTradePanel = useCallback(async () => {
    const orderRequest = orderSliceToOrderRequestForPreTrade({
      appState: store.getState(),
      addToast,
    });

    if (!orderRequest) {
      return;
    }

    const { sessionId, action } = await resolveSession(orderRequest.Symbol);
    if (action === 'cancel') {
      return;
    }

    const request: PreTradeRequest = {
      SessionID: sessionId,
      Order: orderRequest,
    };

    // Clear the user-selected lock so the incoming API response can update the reference.
    dispatch(clearUserSelectedReference());
    const { error } = await requestPreTrade(request);
    if (error) {
      return;
    }

    dispatch(setActiveSession({ sessionId }));

    if (layoutId) {
      navigate(getPreTradeRoute({ layoutId, sessionId, tab: PreTradeTabType.Scenarios }));
    }
  }, [addToast, dispatch, layoutId, navigate, requestPreTrade, resolveSession, store]);

  return { openPreTradePanel, isPreTradeLoading, ConfirmationDialog };
}
